import { useEffect } from 'react';

import getNights from '@shared/utils/getNights';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';
import {
  FlightJourney,
  ReturnFlightJourney,
} from '@shared/v2/services/types/api/parsedResponse';

import { useCustomerSelection } from '@/components/CustomerSelectionProvider';
import dataLayer from '@/v2/utils/dataLayer';
import {
  getEcommerceFlightItem,
  hasReturnFlightJourney,
} from '@/v2/utils/dataLayer/eventDataHelpers';
import { getFlightOriginDestination } from '@/v2/utils/getFlightOriginDestination';

export interface UseFlightViewItemListEventProps {
  flightOptions: Array<FlightJourney | ReturnFlightJourney> | undefined;
  flightDirection?: 'Departing' | 'Returning';
}

const useFlightViewItemListEvent = ({
  flightDirection = 'Departing',
  flightOptions,
}: UseFlightViewItemListEventProps) => {
  const { searchQuery, isReady } = useSearchQuery();
  const { paymentType } = useCustomerSelection();
  useEffect(() => {
    if (!isReady || !searchQuery || !flightOptions) return;
    const duration =
      searchQuery.departureDate && searchQuery.returnDate
        ? getNights(searchQuery)
        : undefined;

    const travellers = {
      adults: searchQuery.adults || 2,
      children: searchQuery.children || 0,
      infants: searchQuery.infants || 0,
    };

    const flightType = flightDirection === 'Departing' ? 'Departure' : 'Return';

    const items = flightOptions.map(
      (flightOption: FlightJourney | ReturnFlightJourney, index) => {
        const hasReturnFlight = hasReturnFlightJourney(flightOption);

        return getEcommerceFlightItem({
          travellers,
          flightOption: flightOption,
          flightType: hasReturnFlight ? 'Departure / Return' : flightType,
          index: index + 1,
        });
      },
    );

    const { origin, destination } = getFlightOriginDestination({
      flightOption: flightOptions[0],
      flightDirection,
    });

    const travelDetails = {
      origin,
      destination,
      duration,
      departureDate: searchQuery.departureDate,
      returnDate: searchQuery.returnDate,
    };

    dataLayer.flightViewItemListEvent({
      items,
      paymentType,
      travellers,
      travelDetails,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, flightDirection, paymentType]);
};

export default useFlightViewItemListEvent;
